/**
 * Renders ManualContent to DOM for PDF/print export.
 */
import type { ManualBlock, ManualContent, InlinePart } from './json-converter';
import { XAMIX_ELEMENT_ROOT } from './schema';

/** Normal: 2em. H1–H3: match xamix inline inside heading (2em × heading size). */
const XAMIX_FONT_SIZES: Record<number, number> = { 0: 2, 1: 4, 2: 3, 3: 2.2 };

function createXamixEl(textInput: string, root: string, level: number = 0, bold: boolean = false, brush: boolean = false, color?: string | null): HTMLElement {
  const el = document.createElement('xamix-element') as HTMLElement & { textInput?: string };
  el.setAttribute('text-input', textInput);
  el.setAttribute('root', root);
  el.setAttribute('font-size', String(XAMIX_FONT_SIZES[level] ?? 2));
  el.setAttribute('unit', 'em');
  if (bold) el.setAttribute('bold', '');
  if (brush) el.setAttribute('brush', '');
  if (color) el.style.color = color;
  el.setAttribute('dir', 'rtl');
  el.style.display = 'inline-block';
  el.style.verticalAlign = 'middle';
  el.style.direction = 'rtl';
  el.style.unicodeBidi = 'embed';
  el.textInput = textInput;
  return el;
}

function wrapWithMarks(content: string, bold?: boolean, italic?: boolean, color?: string, highlight?: string): Text | HTMLElement {
  let node: Text | HTMLElement = document.createTextNode(content ?? '');
  if (italic) {
    const em = document.createElement('em');
    em.appendChild(node);
    node = em;
  }
  if (bold) {
    const strong = document.createElement('strong');
    strong.appendChild(node);
    node = strong;
  }
  if (highlight) {
    const mark = document.createElement('mark');
    mark.style.backgroundColor = highlight;
    // Apply color to mark element when both color and highlight are present
    if (color) {
      mark.style.color = color;
    }
    mark.appendChild(node);
    node = mark;
  } else if (color) {
    // Only wrap in span when there's no highlight
    const span = document.createElement('span');
    span.style.color = color;
    span.appendChild(node);
    node = span;
  }
  return node;
}

/** Text nodes for content; splits on \n to insert <br>, strips trailing spaces with <br>. */
function textWithTrailingBr(content: string, bold?: boolean, italic?: boolean, color?: string, highlight?: string): (Text | HTMLElement)[] {
  const raw = content ?? '';
  const trailingSpaces = raw.match(/\s+$/);
  const textOnly = trailingSpaces ? raw.slice(0, -trailingSpaces[0].length) : raw;
  const nodes: (Text | HTMLElement)[] = [];
  if (textOnly.includes('\n')) {
    const segments = textOnly.split('\n');
    for (let i = 0; i < segments.length; i++) {
      if (segments[i].length > 0) {
        nodes.push(wrapWithMarks(segments[i], bold, italic, color, highlight));
      }
      if (i < segments.length - 1) {
        nodes.push(document.createElement('br'));
      }
    }
  } else if (textOnly.length > 0) {
    nodes.push(wrapWithMarks(textOnly, bold, italic, color, highlight));
  }
  if (trailingSpaces) {
    nodes.push(document.createElement('br'));
  }
  return nodes;
}

function createInlineImage(p: { src?: string; alt?: string; title?: string; width?: number | null; height?: number | null }): HTMLImageElement {
  const img = document.createElement('img');
  img.src = p.src ?? '';
  if (p.alt) img.alt = p.alt;
  if (p.title) img.title = p.title;
  if (p.width != null) img.width = p.width;
  if (p.height != null) img.height = p.height;
  img.style.maxWidth = '100%';
  img.style.verticalAlign = 'middle';
  return img;
}

function createInlineSvgEl(p: { content?: string; width?: number | null; height?: number | null }): HTMLElement {
  const span = document.createElement('span');
  span.style.display = 'inline-block';
  span.style.verticalAlign = 'middle';
  if (p.content) {
    const temp = document.createElement('div');
    temp.innerHTML = p.content;
    const svg = temp.querySelector('svg');
    if (svg) {
      if (p.width != null) svg.setAttribute('width', String(p.width));
      if (p.height != null) svg.setAttribute('height', String(p.height));
      span.appendChild(svg);
    }
  }
  return span;
}

function partsToInlineNodes(parts: InlinePart[], root: string): (Text | HTMLElement)[] {
  const nodes: (Text | HTMLElement)[] = [];
  for (const p of parts ?? []) {
    if (p.type === 'heading') {
      // Handle heading inside table cell
      const level = Math.min(3, Math.max(1, (p.level ?? 1) as number));
      const h = document.createElement(`h${level}` as 'h1' | 'h2' | 'h3');
      if (p.parts && p.parts.length > 0) {
        for (const node of partsToInlineNodes(p.parts, root)) {
          h.appendChild(node);
        }
      } else {
        const headingPart = p as { content?: string; color?: string; highlight?: string };
        for (const node of textWithTrailingBr(headingPart.content ?? '', undefined, undefined, headingPart.color, headingPart.highlight)) {
          h.appendChild(node);
        }
      }
      nodes.push(h);
    } else if (p.type === 'xamix') {
      nodes.push(createXamixEl(p.content, root, 0, !!p.bold, !!(p as { brush?: boolean }).brush, (p as { color?: string }).color));
    } else if (p.type === 'image') {
      nodes.push(createInlineImage(p));
    } else if (p.type === 'svg') {
      nodes.push(createInlineSvgEl(p));
    } else {
      for (const node of textWithTrailingBr(p.content ?? '', (p as { bold?: boolean }).bold, (p as { italic?: boolean }).italic, (p as { color?: string }).color, (p as { highlight?: string }).highlight)) {
        nodes.push(node);
      }
    }
  }
  return nodes;
}

function blockToElement(block: ManualBlock, root: string): HTMLElement | null {
  if (block.type === 'text') {
    const p = document.createElement('p');
    for (const node of textWithTrailingBr(block.content ?? '', block.bold, block.italic, block.color, block.highlight)) {
      p.appendChild(node);
    }
    return p;
  }
  if (block.type === 'xamix') {
    const p = document.createElement('p');
    const el = createXamixEl(block.content ?? '', root, 0, !!block.bold, !!block.brush, block.color);
    p.appendChild(el);
    return p;
  }
  if (block.type === 'xamix_block') {
    const div = document.createElement('div');
    div.className = 'block-xamix' + ((block.level ?? 0) >= 1 && (block.level ?? 0) <= 3 ? ` block-xamix-h${block.level}` : '');
    div.setAttribute('dir', 'rtl');
    div.style.direction = 'rtl';
    div.style.textAlign = 'right';
    const el = createXamixEl(block.content ?? '', root, (block.level ?? 0) as 0 | 1 | 2 | 3, !!block.bold, !!block.brush, block.color);
    div.appendChild(el);
    return div;
  }
  if (block.type === 'heading') {
    const level = Math.min(3, Math.max(1, (block.level ?? 1) as number));
    const h = document.createElement(`h${level}` as 'h1' | 'h2' | 'h3');
    if (block.parts && block.parts.length > 0) {
      for (const node of partsToInlineNodes(block.parts, root)) {
        h.appendChild(node);
      }
    } else {
      for (const node of textWithTrailingBr(block.content ?? '', undefined, undefined, block.color, block.highlight)) {
        h.appendChild(node);
      }
    }
    return h;
  }
  if (block.type === 'centered') {
    const div = document.createElement('div');
    div.setAttribute('data-centered', '');
    div.className = `centered centered-${block.size ?? 'normal'}`;
    div.style.textAlign = 'center';
    if (block.parts && block.parts.length > 0) {
      for (const node of partsToInlineNodes(block.parts, root)) {
        div.appendChild(node);
      }
    } else {
      for (const node of textWithTrailingBr(block.content ?? '', undefined, undefined, block.color, block.highlight)) {
        div.appendChild(node);
      }
    }
    return div;
  }
  if (block.type === 'ordered_list') {
    const ol = document.createElement('ol');
    for (const itemParts of block.items ?? []) {
      const li = document.createElement('li');
      const p = document.createElement('p');
      for (const node of partsToInlineNodes(itemParts, root)) {
        p.appendChild(node);
      }
      li.appendChild(p);
      ol.appendChild(li);
    }
    return ol;
  }
  if (block.type === 'bullet_list') {
    const ul = document.createElement('ul');
    for (const itemParts of block.items ?? []) {
      const li = document.createElement('li');
      const p = document.createElement('p');
      for (const node of partsToInlineNodes(itemParts, root)) {
        p.appendChild(node);
      }
      li.appendChild(p);
      ul.appendChild(li);
    }
    return ul;
  }
  if (block.type === 'table') {
    const table = document.createElement('table');
    table.className = 'print-table';
    if (block.margin) {
      table.style.margin = block.margin;
    }
    if (block.backgroundColor) {
      table.style.backgroundColor = block.backgroundColor;
    }
    if (block.borderColor) {
      table.style.border = `1px solid ${block.borderColor}`;
    }
    const colWidths = block.colWidths;
    if (colWidths && colWidths.length > 0) {
      table.style.tableLayout = 'fixed';
      const totalWidth = colWidths.reduce((a, b) => a + b, 0);
      table.style.width = `${totalWidth}px`;
      const colgroup = document.createElement('colgroup');
      for (const w of colWidths) {
        const col = document.createElement('col');
        col.style.width = `${w}px`;
        colgroup.appendChild(col);
      }
      table.appendChild(colgroup);
    }
    const cellBackgrounds = block.cellBackgrounds ?? [];
    const cellSpans = (block as { cellSpans?: ({ colspan?: number; rowspan?: number } | null)[][] }).cellSpans ?? [];
    const tableBg = block.backgroundColor || undefined;
    let rowIdx = 0;
    for (const rowCells of block.rows ?? []) {
      const tr = document.createElement('tr');
      let colIdx = 0;
      for (const cellParts of rowCells) {
        const td = document.createElement('td');
        const cellSpan = cellSpans[rowIdx]?.[colIdx];
        if (cellSpan?.colspan) {
          td.colSpan = cellSpan.colspan;
        }
        if (cellSpan?.rowspan) {
          td.rowSpan = cellSpan.rowspan;
        }
        const cellBg = cellBackgrounds[rowIdx]?.[colIdx];
        const bg = cellBg ?? tableBg ?? 'transparent';
        td.style.setProperty('background-color', bg, 'important');
        const inner = document.createElement('div');
        inner.className = 'print-table-cell-inner';
        inner.style.minHeight = '100%';
        inner.style.boxSizing = 'border-box';
        if (cellBg) {
          inner.style.setProperty('background-color', cellBg, 'important');
        }
        if (colWidths && colWidths[colIdx] != null) {
          td.style.width = `${colWidths[colIdx]}px`;
          td.style.minWidth = `${colWidths[colIdx]}px`;
          td.style.maxWidth = `${colWidths[colIdx]}px`;
          td.style.boxSizing = 'border-box';
        }
        if (block.borderColor) {
          td.style.borderColor = block.borderColor;
        }
        for (const node of partsToInlineNodes(cellParts ?? [], root)) {
          inner.appendChild(node);
        }
        td.appendChild(inner);
        tr.appendChild(td);
        colIdx++;
      }
      table.appendChild(tr);
      rowIdx++;
    }
    return table;
  }
  if (block.type === 'page_break') {
    return null; // Skip in print - pages are section boundaries
  }
  return null;
}

/** Convert blocks to DOM elements (matches blocksToDocNodes logic for paragraphs). */
function blocksToElements(blocks: ManualBlock[], root: string): HTMLElement[] {
  const elements: HTMLElement[] = [];
  let i = 0;
  while (i < blocks.length) {
    const block = blocks[i];
    if (block.type === 'heading' || block.type === 'centered' || block.type === 'xamix_block' ||
        block.type === 'xamix' || block.type === 'ordered_list' || block.type === 'bullet_list' || block.type === 'table') {
      const el = blockToElement(block, root);
      if (el) elements.push(el);
      i++;
    } else if (block.type === 'text') {
      const p = document.createElement('p');
      while (i < blocks.length) {
        const b = blocks[i];
        if (b.type === 'text') {
          const text = b.content ?? '';
          if (text.includes('\n')) {
            const segments = text.split('\n');
            for (let j = 0; j < segments.length; j++) {
              if (segments[j].length > 0) {
                p.appendChild(wrapWithMarks(segments[j], b.bold, b.italic, b.color, b.highlight));
              }
              if (j < segments.length - 1) {
                p.appendChild(document.createElement('br'));
              }
            }
          } else {
            p.appendChild(wrapWithMarks(text, b.bold, b.italic, b.color, b.highlight));
          }
          i++;
        } else if (b.type === 'xamix') {
          p.appendChild(createXamixEl(b.content ?? '', root, 0, !!b.bold, !!b.brush, b.color));
          i++;
        } else if (b.type === 'image') {
          p.appendChild(createInlineImage(b));
          i++;
        } else if (b.type === 'svg') {
          p.appendChild(createInlineSvgEl(b));
          i++;
        } else {
          break;
        }
      }
      elements.push(p);
    } else {
      i++;
    }
  }
  return elements;
}

/** Create a document for printing: returns a container with all pages. */
export function createPrintDocument(content: ManualContent, xamixRoot: string = XAMIX_ELEMENT_ROOT): HTMLElement {
  const container = document.createElement('div');
  container.className = 'print-manual';
  container.innerHTML = `
    <style>
      .print-manual { font-family: 'Segoe UI', system-ui, sans-serif; font-size: 1rem; line-height: 1.6; max-width: 800px; margin: 0 auto; }
      .print-manual .print-page { page-break-after: always; padding: 1rem 0; }
      .print-manual .print-page:last-child { page-break-after: auto; }
      .print-manual .print-title { margin-bottom: 1.5em; }
      .print-manual h1, .print-manual h2, .print-manual h3 { margin: 1em 0 0.5em; font-weight: 600; }
      .print-manual p { margin: 0 0 0.75em; }
      .print-manual [data-centered] { text-align: center; }
      .print-manual .centered-small { font-size: 0.9em; }
      .print-manual .centered-large { font-size: 1.4em; }
      .print-manual .block-xamix { margin: 0.5em 0; direction: rtl; text-align: right; }
      .print-manual .block-xamix-h1, .print-manual .block-xamix-h2, .print-manual .block-xamix-h3 { font-weight: 600; }
      .print-manual ul, .print-manual ol { margin: 0.5em 0; padding-left: 1.5em; }
      .print-manual li { margin: 0.25em 0; }
      .print-manual .print-table { border-collapse: collapse; width: 100%; margin: 0.5em 0; }
      .print-manual .print-table td { border: 1px solid #333; padding: 0.35em 0.5em; vertical-align: top; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      .print-manual .print-table .print-table-cell-inner { min-height: 100%; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      .print-manual xamix-element { vertical-align: middle; direction: rtl; unicode-bidi: embed; }
      .print-manual mark { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      @media print {
        body > *:not(#pdf-print-overlay) { display: none !important; }
        #pdf-print-overlay { position: static !important; padding: 0 !important; overflow: visible !important; }
      }
    </style>
  `;

  for (const section of content.sections) {
    const pageDiv = document.createElement('div');
    pageDiv.className = 'print-page';
    if (section.backgroundColor) {
      pageDiv.style.backgroundColor = section.backgroundColor;
      pageDiv.style.setProperty('print-color-adjust', 'exact', 'important');
    }
    const docDiv = document.createElement('div');
    docDiv.className = 'manual-doc';
    for (const el of blocksToElements(section.blocks.filter((b) => b.type !== 'page_break'), xamixRoot)) {
      docDiv.appendChild(el);
    }
    pageDiv.appendChild(docDiv);
    container.appendChild(pageDiv);
  }
  return container;
}

function escapeHtml(s: string): string {
  const div = document.createElement('div');
  div.textContent = s;
  return div.innerHTML;
}
